import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Chip, Typography } from "@mui/material";
import FlexPage from "components/layout/FlexPage";
import Breadcrumbs from "components/common/Breadcrumbs";
import Loading from "components/common/Loading";
import useTitle from "components/hooks/useTitle";
import { useListMemberDelegationTasks } from "services/delegation";
import {
  getStatusColor,
  getStatusLabel,
} from "components/delegation/DelegationCard";
import { LEARNING_STEPS } from "components/member/learningSteps";
import LearningStepBanner from "components/member/LearningStepBanner";
import { useMemberDetails } from "components/context/MemberDetailsContext";
import { useUpdateProductPrompts } from "services/nux";
import { ListMemberDelegationTasksRequest } from "protogen/delegation_service_pb";

const STEP = LEARNING_STEPS.find((s) => s.key === "delegations")!;
const BANNER_DISMISS_KEY = `learningStep_${STEP.key}_dismissed`;

const IN_PROGRESS_STATUSES = ["pending", "queued", "in_progress", "delegated"];
const DONE_STATUSES = ["completed", "cancelled"];

const byNewest = (a: { createdSec: bigint }, b: { createdSec: bigint }) =>
  Number(b.createdSec) - Number(a.createdSec);

export default () => {
  useTitle("Helping hands");
  const navigate = useNavigate();
  const { request, data } = useListMemberDelegationTasks();
  const { data: memberData } = useMemberDetails();
  const { request: updateProductPrompts } = useUpdateProductPrompts();
  const productPrompts = memberData?.member?.productPrompts
    ? JSON.parse(memberData.member.productPrompts)
    : {};
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const showBanner =
    !bannerDismissed && productPrompts[BANNER_DISMISS_KEY] !== "dismissed";

  const handleDismissBanner = async () => {
    setBannerDismissed(true);
    await updateProductPrompts({
      key: BANNER_DISMISS_KEY,
      value: "dismissed",
    });
  };

  useEffect(() => {
    request(new ListMemberDelegationTasksRequest({}));
  }, []);

  const allTasks = data?.delegationTasks ?? [];
  const activeTasks = allTasks
    .filter((t) => IN_PROGRESS_STATUSES.includes(t.status))
    .sort(byNewest);
  const doneTasks = allTasks
    .filter((t) => DONE_STATUSES.includes(t.status))
    .sort(byNewest);

  const renderSectionHeader = (label: string) => (
    <Typography
      variant="bodySmall"
      color="text.secondary"
      sx={{ fontWeight: 600 }}
    >
      {label}
    </Typography>
  );

  const renderTaskCard = (
    task: (typeof allTasks)[number],
    { muted = false }: { muted?: boolean } = {},
  ) => (
    <Box
      key={task.ref}
      onClick={() => navigate(`/delegations/${encodeURIComponent(task.ref)}`)}
      sx={(theme) => ({
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: "12px",
        padding: "16px 24px",
        borderRadius: "12px",
        border: `1px solid ${theme.palette.gray[4]}`,
        cursor: "pointer",
        "&:hover": { backgroundColor: "#F5F5F5" },
      })}
    >
      <Typography
        variant="body"
        sx={{ flex: 1 }}
        color={muted ? "text.secondary" : "text.primary"}
      >
        {task.title}
      </Typography>
      <Chip
        label={getStatusLabel(task.status)}
        color={getStatusColor(task.status)}
        size="small"
      />
      <Typography
        variant="bodySmall"
        color="text.secondary"
        sx={{ whiteSpace: "nowrap" }}
      >
        {task.createdSec
          ? new Date(Number(task.createdSec) * 1000).toLocaleDateString()
          : ""}
      </Typography>
    </Box>
  );

  return (
    <FlexPage leftAligned sx={{ width: "100%" }}>
      <Breadcrumbs
        breadcrumbs={[
          { name: "Home", link: "/home" },
          { name: "Helping hands" },
        ]}
      />
      <Typography
        variant="display"
        sx={{ marginBottom: "8px", whiteSpace: "nowrap" }}
      >
        Helping hands
      </Typography>
      {showBanner && (
        <LearningStepBanner step={STEP} onDismiss={handleDismissBanner} />
      )}
      {!data ? (
        <Loading />
      ) : allTasks.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "16px",
          }}
        >
          <Typography variant="body" color="text.secondary">
            {STEP.description}
          </Typography>
          <Button
            variant="contained"
            onClick={() =>
              navigate("/conversations/in-app", {
                state: {
                  prefill:
                    "Can someone call the hotel next week and inquire about early checkin?",
                },
              })
            }
          >
            Delegate your first task
          </Button>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "24px",
            width: "100%",
            maxWidth: "800px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            }}
          >
            {renderSectionHeader("Active")}
            {activeTasks.length === 0 ? (
              <Box
                sx={(theme) => ({
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "16px",
                  padding: "32px 24px",
                  borderRadius: "12px",
                  border: `1px dashed ${theme.palette.gray[4]}`,
                })}
              >
                <Typography variant="body" color="text.secondary">
                  No active tasks right now.
                </Typography>
                <Button
                  variant="contained"
                  onClick={() =>
                    navigate("/conversations/in-app", {
                      state: {
                        prefill:
                          "Can someone call the hotel next week and inquire about early checkin?",
                      },
                    })
                  }
                >
                  Add a new task
                </Button>
              </Box>
            ) : (
              activeTasks.map((task) => renderTaskCard(task))
            )}
          </Box>

          {doneTasks.length > 0 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
              }}
            >
              {renderSectionHeader("Completed")}
              {doneTasks.map((task) => renderTaskCard(task, { muted: true }))}
            </Box>
          )}
        </Box>
      )}
    </FlexPage>
  );
};
